import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Link,
  ListItem,
  Text,
  UnorderedList,
  useToast,
} from "@chakra-ui/react";
import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { t } from "i18next";
import { useCallback } from "react";
import { urls } from "../../urls";
import { CriticalError } from "./CriticalError/CriticalError";
import { useRumError } from "./RumComponentContext/useRumError";
import { isApiErrorDto } from "./isApiErrorDto";
import { isFetchBaseQueryError } from "./isFetchBaseQueryError";

// Dynamic keys referenced via t(e.data.errorCode) below; magic comment keeps langen from pruning them.
// t('DOFFIN_VALIDATION_FAILED') t('DOFFIN_SCHEMATRON_FAILED') t('DOFFIN_SCHEMATRON_ERROR') t('VERSION_CONFLICT')

export const useApiError = () => {
  const toast = useToast();
  const rumError = useRumError();

  const trigger = useCallback(
    (e: unknown) => {
      rumError(e, { message: "Critical error toast was triggered" });
      if (e && isFetchBaseQueryError(e) && isApiErrorDto(e.data)) {
        const fieldErrors = e.data.fieldErrors;
        if (fieldErrors && fieldErrors.length > 0) {
          toast({
            title: t("Whooops!"),
            description: (
              <UnorderedList>
                {fieldErrors.map((f, i) => (
                  <ListItem key={`${f.field}-${i}`}>{t(f.code, f.params ?? {})}</ListItem>
                ))}
              </UnorderedList>
            ),
            status: "error",
          });
        } else {
          toast({
            title: t("Whooops!"),
            description: e.data.errorCode ? t(e.data.errorCode) : t("Something went wrong!"),
            status: "error",
          });
        }
      } else {
        toast({ title: t("Whooops!"), description: t("Something went wrong!"), status: "error" });
      }
    },
    [rumError, toast]
  );

  const triggerSoft = useCallback(
    (e: unknown) => {
      rumError(e, { message: "Soft error toast was triggered" });
      if (e && isFetchBaseQueryError(e) && isApiErrorDto(e.data)) {
        toast({ description: t(e.data.errorCode), status: "error", duration: 3000 });
      } else {
        toast({ description: t("Something went wrong!"), status: "error", duration: 3000 });
      }
    },
    [rumError, toast]
  );

  const message = useCallback(
    (e: unknown): string =>
      (e && isFetchBaseQueryError(e) && isApiErrorDto(e.data) && e.data.errorCode
        ? t(e.data.errorCode)
        : t("Something went wrong!")) ?? "",
    [],
  );

  const show = useCallback(
    (e: FetchBaseQueryError | SerializedError): React.ReactElement => {
      rumError(e, { message: "Critical error message was displayed" });
      if (isFetchBaseQueryError(e) && isApiErrorDto(e.data)) {
        return <CriticalError title={t("Whooops!") ?? undefined} message={t(e.data.errorCode) ?? undefined} />;
      } else {
        return <CriticalError title={t("Whooops!") ?? undefined} message={t("Something went wrong!") ?? undefined} />;
      }
    },
    [rumError]
  );

  const showNoAccess = useCallback(() => {
    return (
      <Box padding="10px">
        <Alert
          status="warning"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          height="200px"
        >
          <AlertIcon boxSize="40px" mr={0} />
          <AlertTitle mt={4} mb={1} fontSize="lg">
            {t("No access!")}
          </AlertTitle>
          <AlertDescription>
            {t("You don't have access to this page")}
            <Text>
              {t("You can")}&nbsp;
              <Link color="smPrimary" href={urls.home}>
                {t("click here")}
              </Link>
            </Text>
            {t("to get back to the home page")}.
          </AlertDescription>
        </Alert>
      </Box>
    );
  }, []);

  return {
    trigger,
    triggerSoft,
    message,
    show,
    showNoAccess,
  };
};
